import { useCallback, useEffect, useMemo, useRef } from "react";
import { useChat } from "@ai-sdk/react";
import { DirectChatTransport, ToolLoopAgent, stepCountIs } from "ai";
import type { UIMessage } from "ai";
import { useChatStore } from "../store/chatStore";
import { useSessionStore } from "../store/sessionStore";
import { useSettingsStore } from "../store/settingsStore";
import type { AppAdapter } from "../types";
import { createCaiProvider, resolveSlashCommand, type CaiAgentConfig } from "../ai";

interface UseAgentOptions {
  adapter: AppAdapter;
  agentConfig: Omit<CaiAgentConfig, "tools" | "modelId" | "maxSteps">;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mcpTools?: Record<string, any>;
}

export function useAgent({ adapter, agentConfig, mcpTools = {} }: UseAgentOptions) {
  const { modelId, maxSteps } = useSettingsStore();
  const { activeSessionId, updateSessionMessages } = useSessionStore();
  const { error: globalError, setError } = useChatStore();

  // Build the agent — recreated when model, tools, or prompt changes
  const agent = useMemo(() => {
    const provider = createCaiProvider({
      baseURL: agentConfig.baseURL,
      getToken: agentConfig.getToken,
    });

    return new ToolLoopAgent({
      model: provider.chat(modelId),
      instructions: adapter.systemPrompt,
      tools: { ...adapter.tools, ...mcpTools },
      stopWhen: stepCountIs(maxSteps),
      prepareCall: async (options) => {
        // Inject document context into the system prompt before each LLM call
        const context = adapter.getContext ? await adapter.getContext() : "";
        if (!context) return options;
        return {
          ...options,
          instructions: `${adapter.systemPrompt}\n\nCurrent document context:\n${context}`,
        };
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, maxSteps, adapter, agentConfig.baseURL, mcpTools]);

  // Keep a ref to the latest agent so the transport always delegates to it.
  // useChat captures the transport at mount and doesn't react to changes,
  // so we use a Proxy to make the transport stable while the agent updates.
  const agentRef = useRef(agent);
  agentRef.current = agent;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stableAgent = useMemo(() => new Proxy({} as any, {
    get(_, prop) {
      const target = agentRef.current;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const value = (target as any)[prop];
      return typeof value === "function" ? value.bind(target) : value;
    },
  }), []);

  // DirectChatTransport runs the agent in-process (no server).
  // Transport is stable — the proxy ensures it always uses the latest agent.
  const transport = useMemo(
    () => new DirectChatTransport({ agent: stableAgent }),
    [stableAgent]
  );

  const {
    messages,
    sendMessage: chatSendMessage,
    setMessages,
    status,
    error: chatError,
  } = useChat({ transport });

  const isStreaming = status === "streaming" || status === "submitted";

  // Sync SDK errors to our global error store
  useEffect(() => {
    if (chatError) {
      setError(chatError.message);
    }
  }, [chatError, setError]);

  // Persist messages to session store when they change
  const sessionIdRef = useRef(activeSessionId);
  sessionIdRef.current = activeSessionId;

  useEffect(() => {
    if (sessionIdRef.current && messages.length > 0 && !isStreaming) {
      updateSessionMessages(sessionIdRef.current, messages);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, isStreaming]);

  // Ref to track last input for retry
  const lastInputRef = useRef<string | null>(null);

  const sendMessage = useCallback(
    (input: string) => {
      lastInputRef.current = input;
      setError(null);

      // Resolve slash command if applicable
      const resolved = resolveSlashCommand(input, adapter.slashCommands);
      const finalInput = resolved ?? input;

      chatSendMessage({ text: finalInput });
    },
    [adapter.slashCommands, chatSendMessage, setError]
  );

  const retryLastMessage = useCallback(() => {
    if (lastInputRef.current && messages.length >= 2) {
      const input = lastInputRef.current;
      // Remove the last user + assistant message pair
      setMessages(messages.slice(0, -2));
      setError(null);
      // Re-send after state update
      setTimeout(() => chatSendMessage({ text: input }), 0);
    }
  }, [messages, setMessages, chatSendMessage, setError]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, [setMessages]);

  return {
    messages,
    isStreaming,
    sendMessage,
    retryLastMessage,
    clearMessages,
    setMessages,
    error: globalError,
  };
}
